'use client';

import { Box, Container, Stack, Text, Button, Field, Input, Link, Alert } from '@chakra-ui/react';
import React from 'react';
import { CanLogoIcon } from '../../../../../public/assets/icons';
import { PasswordInput } from '@/components/ui/password-input';
import { loginStyles } from './login.styles';
import { useForm } from 'react-hook-form';
import { loginSchema, TLoginForm } from './login-form.validator';
import { zodResolver } from '@hookform/resolvers/zod';
import { userLogin } from '@/services/auth/auth.service';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { useSearchParams } from 'next/navigation';

const LoginForm = () => {
   const param = useSearchParams();
   const refer = param.get('refer');
   const { handleLogin } = useAuthFunc();
   const {
      register,
      handleSubmit,
      formState: { errors },
      setError,
   } = useForm<TLoginForm>({
      defaultValues: {
         email: '',
         password: '',
      },
      resolver: zodResolver(loginSchema),
      mode: 'all',
   });

   const onSubmit = handleSubmit(async values => {
      const [error, data] = await userLogin(values);

      if (error) {
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      handleLogin(data.response, refer);
   });

   return (
      <Container css={loginStyles.container}>
         <Box css={loginStyles.reset_area}>
            <Box css={loginStyles.reset_box}>
               <CanLogoIcon />
               <Text as="h2" color="white" textStyle="4xl" fontWeight="bold">
                  Login
               </Text>
               <Text color="darkGray" textStyle="md" textAlign="center" mt="-10px">
                  Welcome Back! Please login your account
               </Text>
               {errors.root?.message && (
                  <Alert.Root status="error">
                     <Alert.Indicator />
                     <Alert.Title>{errors.root.message}</Alert.Title>
                  </Alert.Root>
               )}
               <Box as="form" css={loginStyles.reset_form} onSubmit={onSubmit}>
                  <Stack gap="4" align="flex-start">
                     <Field.Root>
                        <Field.Label textStyle="xs" color="white" mb="1">
                           Email
                           <Box as="span" color="#f00">
                              *
                           </Box>
                        </Field.Label>
                        <Input
                           {...register('email')}
                           placeholder="Username or Email"
                           variant="subtle"
                           h="10"
                           rounded="12px"
                           bg="#202020"
                        />
                        {errors.email && <Text color="error">{errors.email.message}</Text>}
                     </Field.Root>

                     <Field.Root>
                        <Field.Label textStyle="xs" color="white" mb="1">
                           Password
                           <Box as="span" color="#f00">
                              *
                           </Box>
                        </Field.Label>
                        <Box mb="2" w="full">
                           <PasswordInput
                              {...register('password')}
                              placeholder="Password"
                              size="md"
                              h="10"
                              rounded="12px"
                              bg="#202020"
                           />
                        </Box>
                        {errors.password && <Text color="error">{errors.password.message}</Text>}
                     </Field.Root>
                     <Box textAlign="right" w="full" mt="-3" mb="2">
                        <Link
                           href="/auth/forgot-password"
                           color="white"
                           fontSize="sm"
                           fontWeight="bold"
                           _hover={{ textDecoration: 'underline' }}
                        >
                           Forgot Password?
                        </Link>
                     </Box>
                     <Button type="submit" h="10" m="0 auto">
                        Login
                     </Button>
                  </Stack>
               </Box>
            </Box>
         </Box>
      </Container>
   );
};

export default LoginForm;
